import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Pencil } from "lucide-react";
import { api } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { useConfirm } from "../../lib/confirm";
import { apiErrorMessage } from "../../lib/errors";
import { MediaPicker } from "../../components/media-picker";
import { EmptyState } from "../../components/empty-state";

interface Draft {
  id: string;
  title: string | null;
  content: string;
  platform: string | null;
  social_account_id: string | null;
  category_id: string | null;
  hook_id: string | null;
  voice_profile_id: string | null;
  char_count: number;
  created_at: number;
  updated_at: number;
}

interface SocialAccount {
  id: string;
  platform: string;
  handle: string;
  status: string;
}

const PLATFORM_LABEL: Record<string, string> = {
  twitter: "X / Twitter",
  bluesky: "Bluesky",
  linkedin: "LinkedIn",
  newsletter: "Newsletter",
  short_video: "Short video",
};

const PLATFORM_LIMIT: Record<string, number> = {
  twitter: 270,
  bluesky: 280,
  linkedin: 2900,
  newsletter: 10000,
  short_video: 600,
};

const SCRIPT_ONLY = new Set<string>(["newsletter", "short_video"]);
function isScriptOnlyPlatform(p: string): boolean {
  return SCRIPT_ONLY.has(p);
}

function downloadAsTextFile(filename: string, content: string) {
  const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const PLATFORM_FILTERS: { value: string; label: string }[] = [
  { value: "", label: "All" },
  { value: "twitter", label: "X" },
  { value: "bluesky", label: "Bluesky" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "newsletter", label: "Newsletter" },
  { value: "short_video", label: "Short video" },
];

function relativeTime(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  const days = Math.floor(diff / 86_400_000);
  if (days < 30) return `${days}d ago`;
  return new Date(ms).toLocaleDateString();
}

function preview(s: string, n = 140): string {
  const trimmed = s.trim();
  return trimmed.length <= n ? trimmed : trimmed.slice(0, n - 1) + "…";
}

export default function DraftsPage() {
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [loading, setLoading] = useState(true);
  const [platformFilter, setPlatformFilter] = useState<string>("");
  const [editingId, setEditingId] = useState<string | null>(
    params.get("id") ?? null
  );

  async function load() {
    setLoading(true);
    try {
      const qs = platformFilter ? `?platform=${platformFilter}` : "";
      const res = await api.get<{ drafts: Draft[] }>(`/api/drafts${qs}`);
      setDrafts(res.drafts ?? []);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformFilter]);

  async function createNew() {
    try {
      const res = await api.post<{ id: string }>("/api/drafts", {
        content: "",
      });
      setEditingId(res.id);
      const next = new URLSearchParams(params);
      next.set("id", res.id);
      setParams(next);
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  }

  async function remove(id: string) {
    const ok = await confirm({
      title: "Delete draft",
      message: "This draft will be permanently deleted.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.del(`/api/drafts/${id}`);
      if (editingId === id) {
        setEditingId(null);
        const next = new URLSearchParams(params);
        next.delete("id");
        setParams(next);
      }
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  }

  function open(id: string) {
    setEditingId(id);
    const next = new URLSearchParams(params);
    next.set("id", id);
    setParams(next);
  }

  function close() {
    setEditingId(null);
    const next = new URLSearchParams(params);
    next.delete("id");
    setParams(next);
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="page-title">Drafts</h1>
          <p className="page-subtitle">
            Quick compose with autosave. Pick up where you left off, or schedule
            when ready.
          </p>
        </div>
        <button onClick={createNew} className="btn-brand">
          + New draft
        </button>
      </header>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="text-xs text-ink-500">Platform:</span>
        {PLATFORM_FILTERS.map((p) => (
          <button
            key={p.value || "all"}
            onClick={() => setPlatformFilter(p.value)}
            className={`rounded-full px-3 py-1 text-xs ${
              platformFilter === p.value
                ? "bg-ink-900 text-white"
                : "bg-ink-100 text-ink-600 hover:bg-ink-200"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-ink-500">Loading…</p>
      ) : drafts.length === 0 ? (
        <EmptyState
          icon={<Pencil className="h-6 w-6" />}
          title="No drafts yet"
          description="Drafts auto-save as you type — start one for that idea you'll polish later, or hit New draft to begin from scratch."
          action={
            <button onClick={createNew} className="btn-brand">
              + New draft
            </button>
          }
        />
      ) : (
        <ul className="space-y-2">
          {drafts.map((d) => (
            <li
              key={d.id}
              className="cursor-pointer rounded-xl border border-ink-200/70 bg-white p-4 shadow-xs transition-all hover:-translate-y-0.5 hover:border-ink-200 hover:shadow-md"
              onClick={() => open(d.id)}
            >
              <div className="mb-1 flex flex-wrap items-center gap-2 text-xs">
                {d.platform ? (
                  <span className="rounded-full bg-ink-100 px-2 py-0.5 text-ink-700">
                    {PLATFORM_LABEL[d.platform] ?? d.platform}
                  </span>
                ) : (
                  <span className="rounded-full bg-ink-50 px-2 py-0.5 text-ink-400">
                    no platform
                  </span>
                )}
                <span className="text-ink-500">{d.char_count} chars</span>
                <span className="ml-auto text-ink-400">
                  {relativeTime(d.updated_at)}
                </span>
              </div>
              {d.title ? (
                <h3 className="text-sm font-semibold text-ink-900">
                  {d.title}
                </h3>
              ) : null}
              <p className="mt-1 line-clamp-2 whitespace-pre-wrap text-sm text-ink-700">
                {d.content.trim() ? preview(d.content) : (
                  <span className="italic text-ink-400">empty draft</span>
                )}
              </p>
              <div className="mt-2 flex justify-end">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    void remove(d.id);
                  }}
                  className="text-xs text-red-600 hover:underline"
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editingId ? (
        <DraftEditor
          id={editingId}
          onClose={close}
          onChanged={load}
          onScheduled={() => {
            close();
            void load();
            navigate("/app/schedule");
          }}
        />
      ) : null}
    </div>
  );
}

function DraftEditor(props: {
  id: string;
  onClose: () => void;
  onChanged: () => void;
  onScheduled: () => void;
}) {
  const toast = useToast();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [content, setContent] = useState("");
  const [title, setTitle] = useState("");
  const [platform, setPlatform] = useState<string>("");
  const [accountId, setAccountId] = useState("");
  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [scheduleFor, setScheduleFor] = useState("");
  const [mediaIds, setMediaIds] = useState<string[]>([]);
  const debounceRef = useRef<number | null>(null);
  const dirtyRef = useRef(false);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get<{ draft: Draft }>(`/api/drafts/${props.id}`),
      api.get<{ social_accounts: SocialAccount[] }>("/api/social-accounts"),
    ])
      .then(([d, a]) => {
        if (!alive) return;
        setDraft(d.draft);
        setContent(d.draft.content);
        setTitle(d.draft.title ?? "");
        setPlatform(d.draft.platform ?? "");
        setAccountId(d.draft.social_account_id ?? "");
        setSavedAt(d.draft.updated_at);
        setAccounts((a.social_accounts ?? []).filter((x) => x.status === "active"));
      })
      .catch((err) => {
        toast.error(apiErrorMessage(err));
        props.onClose();
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.id]);

  const accountsForPlatform = useMemo(
    () => (platform ? accounts.filter((a) => a.platform === platform) : []),
    [platform, accounts]
  );

  async function flushSave() {
    if (!dirtyRef.current) return;
    dirtyRef.current = false;
    setSaving(true);
    try {
      const res = await api.patch<{ updated_at: number }>(
        `/api/drafts/${props.id}`,
        {
          title: title.trim() || null,
          content,
          platform: platform || null,
          social_account_id: accountId || null,
        }
      );
      setSavedAt(res.updated_at);
      props.onChanged();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  function scheduleSave() {
    dirtyRef.current = true;
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      void flushSave();
    }, 1200);
  }

  useEffect(() => {
    if (!draft) return;
    if (
      content !== draft.content ||
      (title || null) !== (draft.title || null) ||
      (platform || null) !== (draft.platform || null) ||
      (accountId || null) !== (draft.social_account_id || null)
    ) {
      scheduleSave();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, title, platform, accountId]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      if (dirtyRef.current) void flushSave();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function scheduleNow() {
    if (!content.trim()) {
      toast.error("Draft is empty.");
      return;
    }
    if (!platform) {
      toast.error("Pick a platform first.");
      return;
    }
    if (!accountId) {
      toast.error("Pick an account first.");
      return;
    }
    if (!scheduleFor) {
      toast.error("Pick a date/time.");
      return;
    }
    const ts = new Date(scheduleFor).getTime();
    if (!Number.isFinite(ts) || ts <= Date.now() + 30_000) {
      toast.error("Schedule must be at least 30 seconds in the future.");
      return;
    }

    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
      await flushSave();
    }
    setScheduling(true);
    try {
      await api.post("/api/scheduled-posts", {
        social_account_id: accountId,
        platform,
        content,
        scheduled_for: ts,
        media_asset_ids: mediaIds.length > 0 ? mediaIds : undefined,
      });
      await api.del(`/api/drafts/${props.id}`);
      toast.success("Draft scheduled.");
      props.onScheduled();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setScheduling(false);
    }
  }

  const charCount = [...content].length;
  const limit = PLATFORM_LIMIT[platform] ?? 0;
  const overLimit = limit > 0 && charCount > limit;
  const minDateTime = (() => {
    const d = new Date(Date.now() + 60_000);
    const off = d.getTimezoneOffset() * 60_000;
    return new Date(d.getTime() - off).toISOString().slice(0, 16);
  })();

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-center bg-ink-900/40 p-4 backdrop-blur-sm animate-fade-in sm:items-center"
      onClick={() => {
        if (debounceRef.current) {
          window.clearTimeout(debounceRef.current);
          void flushSave();
        }
        props.onClose();
      }}
    >
      <div
        className="flex max-h-[95vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-ink-200/60 bg-white shadow-xl animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-ink-200 px-4 py-3">
          <div className="flex items-center gap-2 text-sm">
            <h3 className="font-semibold text-ink-900">Edit draft</h3>
            <span className="text-xs text-ink-500">
              {saving
                ? "Saving…"
                : savedAt
                  ? `Saved ${relativeTime(savedAt)}`
                  : ""}
            </span>
          </div>
          <button
            onClick={() => {
              if (debounceRef.current) {
                window.clearTimeout(debounceRef.current);
                void flushSave();
              }
              props.onClose();
            }}
            className="text-ink-500 hover:text-ink-900"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="mb-3">
            <label className="mb-1 block text-xs text-ink-500">Title (optional)</label>
            <input
              className="input w-full"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Pricing experiment recap"
              maxLength={200}
            />
          </div>

          <div className="mb-3 grid grid-cols-2 gap-2">
            <div>
              <label className="mb-1 block text-xs text-ink-500">Platform</label>
              <select
                className="input w-full"
                value={platform}
                onChange={(e) => {
                  setPlatform(e.target.value);
                  setAccountId("");
                }}
              >
                <option value="">— pick later —</option>
                <option value="twitter">X / Twitter</option>
                <option value="bluesky">Bluesky</option>
                <option value="linkedin">LinkedIn</option>
                <option value="newsletter">Newsletter</option>
                <option value="short_video">Short video</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs text-ink-500">Account</label>
              <select
                className="input w-full"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                disabled={
                  !platform ||
                  isScriptOnlyPlatform(platform) ||
                  accountsForPlatform.length === 0
                }
              >
                <option value="">
                  {isScriptOnlyPlatform(platform) ? "— not needed —" : "— none —"}
                </option>
                {accountsForPlatform.map((a) => (
                  <option key={a.id} value={a.id}>
                    @{a.handle}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <textarea
            className="input min-h-[240px] w-full font-mono"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Write your post here…"
          />
          <div className="mt-1 flex items-center justify-between text-xs">
            <span className={overLimit ? "text-red-600" : "text-ink-500"}>
              {charCount} chars
              {limit > 0 ? ` / ${limit}` : ""}
              {overLimit ? " · over limit (will auto-thread or be rejected)" : ""}
            </span>
          </div>

          {!isScriptOnlyPlatform(platform) ? (
            <div className="mt-3">
              <label className="mb-1.5 block text-xs text-ink-500">
                Images <span className="text-ink-400">(optional)</span>
              </label>
              <MediaPicker
                value={mediaIds}
                onChange={setMediaIds}
                max={4}
                disabled={scheduling}
              />
            </div>
          ) : null}
        </div>

        <div className="border-t border-ink-200 bg-ink-50 px-4 py-3">
          {isScriptOnlyPlatform(platform) ? (
            <div className="space-y-3">
              <div className="rounded-lg border border-amber-200/70 bg-amber-50/60 px-3.5 py-2.5 text-xs text-amber-900">
                <strong className="font-semibold">Script only.</strong>{" "}
                {platform === "newsletter"
                  ? "BrandEcho doesn't send email. Copy or download to use in Beehiiv / Substack / ConvertKit."
                  : "BrandEcho doesn't post video. Copy or download as the script for your TikTok / Reels / Shorts."}
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => {
                    void navigator.clipboard.writeText(content);
                    toast.success("Copied to clipboard.");
                  }}
                  disabled={!content.trim()}
                  className="btn-secondary"
                >
                  Copy
                </button>
                <button
                  type="button"
                  onClick={() =>
                    downloadAsTextFile(
                      `${platform}-${props.id.slice(-6)}.txt`,
                      content
                    )
                  }
                  disabled={!content.trim()}
                  className="btn-primary"
                >
                  Download .txt
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <input
                type="datetime-local"
                className="input w-full"
                value={scheduleFor}
                min={minDateTime}
                onChange={(e) => setScheduleFor(e.target.value)}
              />
              <button
                onClick={scheduleNow}
                disabled={scheduling || !content.trim() || !platform || !accountId || !scheduleFor}
                className="btn-primary"
              >
                {scheduling ? "Scheduling…" : "Schedule"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
